import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutDashboard, CalendarDays, Users, Settings, LogOut, ExternalLink, Mail, Code2, Clock, Star, GripVertical, RotateCcw, ShieldCheck, ChevronLeft, ChevronRight, Tag, ShieldAlert, Archive, ScrollText } from "lucide-react";

const NAV_ITEMS_DEFAULT = [
  { href: "/admin/dashboard",        label: "Dashboard",        icon: LayoutDashboard },
  { href: "/admin/bookings",         label: "Bookings",         icon: CalendarDays    },
  { href: "/admin/volunteers",       label: "Volunteers",       icon: Users           },
  { href: "/admin/booking-codes",    label: "Booking Codes",    icon: Tag             },
  { href: "/admin/volunteer-hours",  label: "Volunteer Hours",  icon: Clock           },
  { href: "/admin/testimonials",     label: "Testimonials",     icon: Star            },
  { href: "/admin/settings",         label: "Settings",         icon: Settings        },
  { href: "/admin/email-admin",      label: "Email Admin",      icon: Mail            },
  { href: "/admin/email-logs",       label: "Email Log",        icon: ScrollText      },
  { href: "/admin/embed",            label: "Embed on Site",    icon: Code2           },
];

const SUPER_ADMIN_ITEM = { href: "/admin/super-admin", label: "Super Admin", icon: ShieldCheck };
const ARCHIVES_ITEM = { href: "/admin/booking-archives", label: "Booking Archives", icon: Archive };
const STORAGE_KEY      = "admin_nav_order";
const COLLAPSED_KEY    = "admin_sidebar_collapsed";

function loadOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!Array.isArray(saved) || saved.length !== NAV_ITEMS_DEFAULT.length) return null;
    const map        = Object.fromEntries(NAV_ITEMS_DEFAULT.map((i) => [i.href, i]));
    const rehydrated = saved.map((href) => map[href]).filter(Boolean);
    return rehydrated.length === NAV_ITEMS_DEFAULT.length ? rehydrated : null;
  } catch { return null; }
}

function saveOrder(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items.map((i) => i.href)));
}

export default function AdminLayout({ children }) {
  const navigate     = useNavigate();
  const location     = useLocation();
  const adminEmail   = localStorage.getItem("admin_email") || "";
  const isSuperAdmin = localStorage.getItem("admin_is_super") === "1";

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "1");
  const [navItems, setNavItems]   = useState(() => loadOrder() || NAV_ITEMS_DEFAULT);
  const [dragOver, setDragOver]   = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const dragItem                  = useRef(null);

  useEffect(() => {
    api.getMaintenanceStatus()
      .then((r) => setMaintenanceMode(r.data.maintenance_mode))
      .catch(() => { /* non-critical */ });
  }, [location.pathname]);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    localStorage.removeItem("admin_is_super");
    navigate("/admin");
  };

  const handleDragStart = (href) => { dragItem.current = href; };
  const handleDragOver  = (e, href) => { e.preventDefault(); if (href !== dragItem.current) setDragOver(href); };
  const handleDrop      = (e, targetHref) => {
    e.preventDefault();
    const from = dragItem.current;
    if (!from || from === targetHref) { setDragOver(null); return; }
    setNavItems((prev) => {
      const next     = [...prev];
      const fromIdx  = next.findIndex((i) => i.href === from);
      const toIdx    = next.findIndex((i) => i.href === targetHref);
      const [moved]  = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      saveOrder(next);
      return next;
    });
    dragItem.current = null;
    setDragOver(null);
  };
  const handleDragEnd = () => { dragItem.current = null; setDragOver(null); };

  const resetOrder = () => { localStorage.removeItem(STORAGE_KEY); setNavItems(NAV_ITEMS_DEFAULT); };
  const isReordered = JSON.stringify(navItems.map((i) => i.href)) !== JSON.stringify(NAV_ITEMS_DEFAULT.map((i) => i.href));

  return (
    <div style={{ fontFamily: "'Public Sans', sans-serif" }} className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className="relative bg-white border-r border-green-100 flex flex-col shadow-sm shrink-0 transition-all duration-300 ease-in-out"
        style={{ width: collapsed ? "64px" : "240px" }}
      >
        {/* Header */}
        <div className={`p-4 border-b border-green-100 flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
            <img src="/favicon.png" alt="DVC" className="w-8 h-8" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className="font-bold text-slate-800 text-sm leading-tight whitespace-nowrap" style={{ fontFamily: "'Outfit', sans-serif" }}>Dodder Valley</p>
              <p className="text-slate-400 text-xs whitespace-nowrap">Admin Panel</p>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto" data-testid="admin-nav">
          {navItems.map(({ href, label, icon: Icon }) => {
            const active = location.pathname === href;
            const isOver = dragOver === href;
            return (
              <div
                key={href}
                draggable={!collapsed}
                onDragStart={() => handleDragStart(href)}
                onDragOver={(e) => handleDragOver(e, href)}
                onDrop={(e) => handleDrop(e, href)}
                onDragEnd={handleDragEnd}
                title={collapsed ? label : undefined}
                className={`group flex items-center rounded-xl transition-all ${isOver ? "ring-2 ring-green-400 ring-offset-1 bg-green-50/60" : ""}`}
              >
                {!collapsed && (
                  <span className="pl-1 pr-0.5 py-2.5 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-40 transition-opacity shrink-0">
                    <GripVertical className="w-3.5 h-3.5 text-slate-400" />
                  </span>
                )}
                <button
                  onClick={() => navigate(href)}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex-1 flex items-center gap-3 rounded-xl text-sm font-medium transition-all text-left
                    ${collapsed ? "justify-center p-3" : "px-2 py-2.5"}
                    ${active
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"}`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${active ? "text-green-700" : "text-slate-400"}`} />
                  {!collapsed && <span className="truncate">{label}</span>}
                </button>
              </div>
            );
          })}

          {/* Super Admin item */}
          {isSuperAdmin && (() => {
            const item   = SUPER_ADMIN_ITEM;
            const active = location.pathname === item.href;
            return (
              <div key={item.href} className="mt-1 border-t border-slate-100 pt-1" title={collapsed ? item.label : undefined}>
                <button
                  onClick={() => navigate(item.href)}
                  data-testid="nav-super-admin"
                  className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all text-left
                    ${collapsed ? "justify-center p-3" : "px-3 py-2.5"}
                    ${active
                      ? "bg-amber-50 text-amber-800 border border-amber-200"
                      : "text-amber-600 hover:bg-amber-50 hover:text-amber-800"}`}
                >
                  <item.icon className={`w-4 h-4 shrink-0 ${active ? "text-amber-700" : "text-amber-400"}`} />
                  {!collapsed && item.label}
                </button>
              </div>
            );
          })()}

          {/* Booking Archives item */}
          {(() => {
            const item = ARCHIVES_ITEM;
            const active = location.pathname === item.href;
            return (
              <div key={item.href} className="mt-1" title={collapsed ? item.label : undefined}>
                <button
                  onClick={() => navigate(item.href)}
                  data-testid="nav-booking-archives"
                  className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all text-left
                    ${collapsed ? "justify-center p-3" : "px-3 py-2.5"}
                    ${active
                      ? "bg-slate-200 text-slate-800 border border-slate-300"
                      : "text-slate-400 hover:bg-slate-50 hover:text-slate-600"}`}
                >
                  <item.icon className={`w-4 h-4 shrink-0 ${active ? "text-slate-600" : "text-slate-400"}`} />
                  {!collapsed && item.label}
                </button>
              </div>
            );
          })()}

          {/* Reset order — only in expanded mode */}
          {isReordered && !collapsed && (
            <button onClick={resetOrder} data-testid="reset-nav-order-btn"
              className="w-full flex items-center gap-2 px-3 py-1.5 mt-1 text-xs text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-50">
              <RotateCcw className="w-3 h-3" /> Reset order
            </button>
          )}
        </nav>

        {/* Footer */}
        <div className={`p-2 border-t border-green-100 space-y-1 ${collapsed ? "flex flex-col items-center" : ""}`}>
          {!collapsed && (
            <a href="/" target="_blank" rel="noreferrer"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all">
              <ExternalLink className="w-4 h-4 text-slate-400" />
              View Booking Page
            </a>
          )}
          {collapsed && (
            <a href="/" target="_blank" rel="noreferrer" title="View Booking Page"
              className="p-3 rounded-xl text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all flex items-center justify-center">
              <ExternalLink className="w-4 h-4 text-slate-400" />
            </a>
          )}
          <button onClick={handleLogout} data-testid="logout-btn" title={collapsed ? "Sign Out" : undefined}
            className={`flex items-center gap-3 rounded-xl text-sm text-red-500 hover:bg-red-50 transition-all
              ${collapsed ? "p-3 justify-center w-full" : "w-full px-3 py-2.5"}`}>
            <LogOut className="w-4 h-4" />
            {!collapsed && "Sign Out"}
          </button>
          {!collapsed && <p className="px-3 py-1 text-xs text-slate-400 truncate">{adminEmail}</p>}
        </div>

        {/* Collapse toggle — anchored to the right edge */}
        <button
          onClick={toggleCollapse}
          data-testid="sidebar-toggle-btn"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-green-700 hover:border-green-300 transition-all z-10"
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto min-w-0">
        {maintenanceMode && (
          <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium" data-testid="maintenance-banner">
            <ShieldAlert className="w-4 h-4" />
            Maintenance mode is active — public bookings are disabled
          </div>
        )}
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}

